import { Component, ElementRef, OnChanges, OnInit, SimpleChanges, forwardRef, ChangeDetectionStrategy, inject, input, output, viewChild , model} from '@angular/core';

import { FormsModule, ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

export interface MultiSelectorOption {
  id: string;
  label: string;
  description?: string;
  icon?: string;
  disabled?: boolean;
  data?: any;
}

export type SearchFn = (option: MultiSelectorOption, searchTerm: string) => boolean;

@Component({
  selector: 'app-multi-value-selector',
  host: { '(document:click)': 'onDocumentClick($event)' },
  imports: [FormsModule],
  templateUrl: './multi-value-selector.component.html',
  styleUrl: './multi-value-selector.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MultiValueSelectorComponent),
      multi: true
    }
  ]
})
export class MultiValueSelectorComponent implements OnInit, OnChanges, ControlValueAccessor {
  private elementRef = inject(ElementRef);

  readonly searchInput = viewChild.required<ElementRef<HTMLInputElement>>('searchInput');

  // Inputs
  readonly options = input<MultiSelectorOption[]>([]);
  readonly placeholder = input<string>('Select options');
  readonly searchPlaceholder = input<string>('Search...');
  readonly emptyMessage = input<string>('No options found');
  readonly disabled = model<boolean>(false);
  readonly showSearch = input<boolean>(true);
  readonly showIcon = input<boolean>(true);
  readonly clearable = input<boolean>(true);
  readonly maxDisplayChips = input<number>(3);
  readonly selectAllLabel = input<string>('Select All');
  readonly showSelectAll = input<boolean>(true);
  readonly searchFn = input<SearchFn>();

  // Outputs
  readonly selectionChange = output<MultiSelectorOption[]>();
  readonly search = output<string>();

  // Internal state
  isOpen = false;
  searchTerm = '';
  filteredOptions: MultiSelectorOption[] = [];
  selectedOptions: MultiSelectorOption[] = [];
  highlightedIndex = -1;

  // ControlValueAccessor
  private onChange: (value: string[]) => void = () => { /* set by registerOnChange */ };
  private onTouched: () => void = () => { /* set by registerOnTouched */ };

  ngOnInit(): void {
    this.filteredOptions = [...this.options()];
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['options']) {
      this.filterOptions();
      // Re-sync selected options with new options
      this.selectedOptions = this.selectedOptions.filter(selected =>
        this.options().some(opt => opt.id === selected.id)
      );
    }
  }

  // ControlValueAccessor implementation
  writeValue(value: string[]): void {
    if (Array.isArray(value)) {
      this.selectedOptions = this.options().filter(opt => value.includes(opt.id));
    } else {
      this.selectedOptions = [];
    }
  }

  registerOnChange(fn: (value: string[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }

  // Getters
  get displayValue(): string {
    if (this.selectedOptions.length === 0) return '';
    if (this.selectedOptions.length <= this.maxDisplayChips()) {
      return this.selectedOptions.map(o => o.label).join(', ');
    }
    return `${this.selectedOptions.slice(0, this.maxDisplayChips()).map(o => o.label).join(', ')} +${this.selectedOptions.length - this.maxDisplayChips()} more`;
  }

  get hasSelection(): boolean {
    return this.selectedOptions.length > 0;
  }

  get allSelected(): boolean {
    const enabledOptions = this.options().filter(o => !o.disabled);
    return enabledOptions.length > 0 && this.selectedOptions.length === enabledOptions.length;
  }

  get someSelected(): boolean {
    return this.selectedOptions.length > 0 && !this.allSelected;
  }

  // Actions
  onTriggerClick(_event: Event): void {
    if (this.disabled()) return;
    if (!this.isOpen) {
      this.openDropdown();
    }
  }

  toggleDropdown(event: Event): void {
    event.stopPropagation();
    if (this.disabled()) return;
    if (this.isOpen) {
      this.closeDropdown();
    } else {
      this.openDropdown();
    }
  }

  openDropdown(): void {
    if (this.disabled() || this.isOpen) return;
    this.isOpen = true;
    this.searchTerm = '';
    this.filterOptions();
    this.highlightedIndex = -1;
    this.onTouched();
    setTimeout(() => this.searchInput()?.nativeElement?.focus(), 0);
  }

  closeDropdown(): void {
    this.isOpen = false;
    this.searchTerm = '';
    this.highlightedIndex = -1;
  }

  clearSearch(event: Event): void {
    event.stopPropagation();
    this.searchTerm = '';
    this.onSearchChange();
    this.searchInput()?.nativeElement?.focus();
  }

  selectOption(option: MultiSelectorOption): void {
    if (option.disabled) return;

    const index = this.selectedOptions.findIndex(o => o.id === option.id);
    if (index >= 0) {
      this.selectedOptions.splice(index, 1);
      this.selectedOptions = [...this.selectedOptions];
    } else {
      this.selectedOptions = [...this.selectedOptions, option];
    }
    this.emitChange();
  }

  toggleSelectAll(): void {
    const enabledOptions = this.options().filter(o => !o.disabled);
    if (this.allSelected) {
      this.selectedOptions = [];
    } else {
      this.selectedOptions = [...enabledOptions];
    }
    this.emitChange();
  }

  clearSelection(event?: Event): void {
    event?.stopPropagation();
    this.selectedOptions = [];
    this.emitChange();
  }

  removeOption(option: MultiSelectorOption, event: Event): void {
    event.stopPropagation();
    this.selectedOptions = this.selectedOptions.filter(o => o.id !== option.id);
    this.emitChange();
  }

  isSelected(option: MultiSelectorOption): boolean {
    return this.selectedOptions.some(o => o.id === option.id);
  }

  onSearchChange(): void {
    this.filterOptions();
    this.highlightedIndex = this.filteredOptions.length > 0 ? 0 : -1;
    this.search.emit(this.searchTerm);
  }

  filterOptions(): void {
    if (!this.searchTerm.trim()) {
      this.filteredOptions = [...this.options()];
    } else if (this.searchFn()) {
      this.filteredOptions = this.options().filter(option => this.searchFn()!(option, this.searchTerm));
    } else {
      this.filteredOptions = this.options().filter(option => this.defaultSearch(option, this.searchTerm));
    }
  }

  private defaultSearch(option: MultiSelectorOption, searchTerm: string): boolean {
    const term = searchTerm.toLowerCase();
    return option.label.toLowerCase().includes(term) || (option.description?.toLowerCase().includes(term) ?? false);
  }

  private emitChange(): void {
    const ids = this.selectedOptions.map(o => o.id);
    this.onChange(ids);
    this.selectionChange.emit([...this.selectedOptions]);
  }

  // Keyboard navigation
  onKeyDown(event: KeyboardEvent): void {
    if (!this.isOpen) {
      if (event.key === 'Enter' || event.key === ' ' || event.key === 'ArrowDown') {
        event.preventDefault();
        this.openDropdown();
      }
      return;
    }

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.highlightNext();
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.highlightPrevious();
        break;
      case 'Enter':
        event.preventDefault();
        if (this.highlightedIndex >= 0 && this.highlightedIndex < this.filteredOptions.length) {
          this.selectOption(this.filteredOptions[this.highlightedIndex]);
        }
        break;
      case 'Escape':
        event.preventDefault();
        this.closeDropdown();
        break;
      case 'Tab':
        this.closeDropdown();
        break;
    }
  }

  highlightNext(): void {
    const enabledOptions = this.filteredOptions.filter(o => !o.disabled);
    if (enabledOptions.length === 0) return;

    let nextIndex = this.highlightedIndex + 1;
    while (nextIndex < this.filteredOptions.length && this.filteredOptions[nextIndex].disabled) {
      nextIndex++;
    }
    if (nextIndex < this.filteredOptions.length) {
      this.highlightedIndex = nextIndex;
      this.scrollToHighlighted();
    }
  }

  highlightPrevious(): void {
    let prevIndex = this.highlightedIndex - 1;
    while (prevIndex >= 0 && this.filteredOptions[prevIndex].disabled) {
      prevIndex--;
    }
    if (prevIndex >= 0) {
      this.highlightedIndex = prevIndex;
      this.scrollToHighlighted();
    }
  }

  scrollToHighlighted(): void {
    setTimeout(() => {
      const dropdown = this.elementRef.nativeElement.querySelector('.dropdown-options');
      const highlighted = dropdown?.querySelector('.option.highlighted');
      if (highlighted && dropdown) {
        highlighted.scrollIntoView({ block: 'nearest' });
      }
    }, 0);
  }

  // Click outside to close
  onDocumentClick(event: MouseEvent): void {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.closeDropdown();
    }
  }
}